import { useSandboxEditor } from "features/editor/Feature";
import { numberToVisibility } from "features/editor/sandboxUtils";
import { hasSandboxPermission } from "isomorphic/permission";
import { capitalize } from "lodash";
import { Stack, Text, Icon, Paragraph } from "prism-react";

import { paths } from "utils/router";

import { ShareContainer } from "../ShareContainer";

import { useSandboxCollaborators } from "./api";
import { Contributors } from "./components/Contributors";
import { InviteForm } from "./components/InviteForm";
import { SandboxVisbility } from "./components/SandboxVisibility";

export const ShareSandboxUI: React.FC<{
  editorURL: URL;
}> = ({ editorURL }) => {
  const editor = useSandboxEditor();

  const [collaboratorsData, refetchCollaborators] = useSandboxCollaborators(
    editor.id,
  );

  const hasPermissionToChange = hasSandboxPermission(
    editor.authorization,
    "write_project",
  );

  const boxType = editor.isCloud ? "Devbox" : "Sandbox";
  const iconType = () => {
    if (editor.privacy === 0) return "community";
    if (editor.privacy === 1) return "hyperlink";
    return "lockClose";
  };

  const renderContent = () => {
    if (hasPermissionToChange) {
      return (
        <Stack gap={3}>
          <Stack horizontal>
            <Icon size={3} type="user" />
            <Text>Change permissions</Text>
          </Stack>

          <SandboxVisbility hasPermission={hasPermissionToChange} />

          <InviteForm
            canInvite={hasPermissionToChange}
            refetchCollaborators={refetchCollaborators}
          />

          <Contributors
            collaborators={collaboratorsData.data?.sandbox?.collaborators ?? []}
            invitations={collaboratorsData.data?.sandbox?.invitations ?? []}
            refetchCollaborators={refetchCollaborators}
          />
        </Stack>
      );
    }

    return (
      <Stack>
        <Stack horizontal>
          <Icon size={4} type={iconType()} />
          <Text>
            {capitalize(numberToVisibility(editor.privacy)) + " " + boxType}
          </Text>
        </Stack>

        {editor.privacy === 0 && (
          <Paragraph css={{ color: "$neutral-fg-base" }}>
            Anyone can access this {boxType}.
          </Paragraph>
        )}

        {editor.privacy === 1 && (
          <Paragraph css={{ color: "$neutral-fg-base" }}>
            Anyone can access this {boxType} with the link.
          </Paragraph>
        )}

        {/** Only viewers in the workspace will see this (private entity, but no permission to edit) */}
        {editor.privacy === 2 && editor.workspace && (
          <Paragraph css={{ color: "$neutral-fg-base" }}>
            Everyone in{" "}
            <Text
              as="a"
              css={{ textDecoration: "underline !important" }}
              href={paths.dashboard.workspaceSettings(editor.workspace.id)}
              rel="noreferrer noopener"
              target="_blank"
            >
              your workspace
            </Text>{" "}
            has access to this {boxType}. You need need to be an editor in the
            workspace to change the permission level.
          </Paragraph>
        )}
      </Stack>
    );
  };

  return (
    <ShareContainer editorURL={editorURL}>{renderContent()}</ShareContainer>
  );
};
